
import { useState, useCallback, useRef } from "react";
import type { BrandSpec, BrandCheckResult, BrandIssue } from "../../../types";
import { checkBrandCompliance } from "../services/brandChecker";
import { navigateToIssue, fixIssue, fixAllIssues, type NavigateOutcome } from "../services/brandFixer";
import { extractBrand } from "../../apply-brand/services/brandExtractor";
import { getActivePresentationBuffer } from "../../../utils/officePptx";

// ─── State machine ────────────────────────────────────────────────────────────

export type BrandCheckPhase =
  | { phase: "idle" }
  | { phase: "extracting"; filename: string }
  | { phase: "scanning"; current: number; total: number; message: string }
  | { phase: "done"; result: BrandCheckResult; brand: BrandSpec; filename: string }
  | { phase: "error"; message: string; brand?: BrandSpec; filename?: string };

// Pull a { brand, filename } pair out of whichever phase still carries one.
function brandSourceFromState(state: BrandCheckPhase): { brand: BrandSpec; filename: string } | null {
  if (state.phase === "done") {
    return { brand: state.brand, filename: state.filename };
  }
  if (state.phase === "error" && state.brand && state.filename) {
    return { brand: state.brand, filename: state.filename };
  }
  return null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBrandCheck() {
  const [state, setState] = useState<BrandCheckPhase>({ phase: "idle" });
  const [fixedIds, setFixedIds] = useState<Set<string>>(new Set());

  // Keep the last brand + filename so Recheck can re-use them without
  // requiring the user to re-upload or re-select the template source.
  const lastBrandRef = useRef<{ brand: BrandSpec; filename: string } | null>(null);

  // User-supplied Font Size Min/Max override (from the results screen's
  // "Re-run to apply" control). Persists across recheck() calls until reset()
  // clears it back to the brand-derived default.
  const fontSizeRangeRef = useRef<{ min: number; max: number } | null>(null);

  // ── Internal: run scan ───────────────────────────────────────────────────
  const runScan = useCallback(async (brand: BrandSpec, filename: string) => {
    setState({ phase: "scanning", current: 0, total: 0, message: "Initialising…" });
    setFixedIds(new Set()); // discard fixes from previous scan

    try {
      const range = fontSizeRangeRef.current;
      const result = await checkBrandCompliance(brand, {
        onProgress: (message, current, total) => {
          setState({ phase: "scanning", current, total, message });
        },
        ...(range ? { fontSizeMin: range.min, fontSizeMax: range.max } : {}),
      });
      setState({ phase: "done", result, brand, filename });
    } catch (e) {
      setState({
        phase: "error",
        message: e instanceof Error ? e.message : "Brand check failed",
        brand,
        filename,
      });
    }
  }, []);

  // ── Extract from active deck, then scan ─────────────────────────────────
  // Brand Check only ever validates the active presentation against its own
  // extracted theme/master — there is no "upload a separate template" flow.
  const checkFromActive = useCallback(async () => {
    const filename = "Active Presentation";
    setState({ phase: "extracting", filename });
    try {
      const buffer = await getActivePresentationBuffer();
      const brand = await extractBrand(buffer);
      lastBrandRef.current = { brand, filename };
      await runScan(brand, filename);
    } catch (e) {
      setState({
        phase: "error",
        message: e instanceof Error ? e.message : "Brand extraction failed",
      });
    }
  }, [runScan]);

  // ── Recheck: fresh scan using the already-loaded brand spec ─────────────
  // Discards previous scan results before starting — satisfies AC §4.
  const recheck = useCallback(async () => {
    const cached = lastBrandRef.current;

    const brandSource = cached ?? brandSourceFromState(state);

    if (!brandSource) {
      setState({ phase: "error", message: "No brand template loaded — run Brand Check first." });
      return;
    }

    await runScan(brandSource.brand, brandSource.filename);
  }, [state, runScan]);

  // ── Recheck with a custom Font Size Min/Max (the results screen's "Re-run
  // to apply" control) — persists the override so subsequent recheck() calls
  // keep using it until reset(). ─────────────────────────────────────────
  const recheckWithFontRange = useCallback(async (min: number, max: number) => {
    fontSizeRangeRef.current = { min, max };
    await recheck();
  }, [recheck]);

  const reset = useCallback(() => {
    lastBrandRef.current = null;
    fontSizeRangeRef.current = null;
    setFixedIds(new Set());
    setState({ phase: "idle" });
  }, []);

  // ── Navigate ─────────────────────────────────────────────────────────────
  const navigate = useCallback(async (issue: BrandIssue): Promise<NavigateOutcome> => {
    return navigateToIssue(issue);
  }, []);

  // ── Fix single issue ─────────────────────────────────────────────────────
  const fix = useCallback(async (issue: BrandIssue) => {
    const brand = (lastBrandRef.current ?? brandSourceFromState(state))?.brand ?? null;
    if (!brand) return;

    const ok = await fixIssue(issue, brand);
    if (ok) {
      setFixedIds((prev) => new Set([...prev, issue.id]));
    }
  }, [state]);

  // ── Fix all issues in a category ─────────────────────────────────────────
  const fixAll = useCallback(async (issues: BrandIssue[]) => {
    const brand = (lastBrandRef.current ?? brandSourceFromState(state))?.brand ?? null;
    if (!brand) return;

    const ids = await fixAllIssues(issues, brand);
    if (ids.length > 0) {
      setFixedIds((prev) => new Set([...prev, ...ids]));
    }
  }, [state]);

  return {
    state, checkFromActive, recheck, recheckWithFontRange, reset,
    fixedIds, navigate, fix, fixAll,
  };
}
